import {
  Grid,
  GridColumn as Column,
  GridColumnMenuCheckboxFilter,
  GridColumnMenuFilter,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import * as React from "react";
import { process } from "@progress/kendo-data-query";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useNavigate } from "react-router-dom";
import ClientContext from "../../layouts/ClientContext";
import LoadingIndicator from "../../components/LoadingIndicator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useQueryGetClients } from "../../data/services/clientService";
import UserContext from "../../layouts/UserContext";
import OEContext from "../../layouts/OEContext";
import { faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import AuditTrailModal from "../client/AuditTrailModal";
import { getRenewalBadge } from "../../constants/renewalEligibility";
import MedicalOnlyCostNote from "../../components/MedicalOnlyCostNote";
import AetnaRenewalNoticeModal from "../../components/AetnaRenewalNoticeModal";
import useAnyClientFeatureAccess from "../../hooks/useAnyClientFeatureAccess";
import useBlockedAwareMessage from "../../hooks/useBlockedAwareMessage";
import { FEATURE_KEYS } from "../../constants";
import {
  isAetnaNoticeDismissed,
  markAetnaNoticeDismissed,
} from "../../utils/aetnaRenewalNotice";

const ClientBrokerDashboard = () => {
  const navigate = useNavigate();
  const { setClient } = React.useContext(ClientContext);
  const { loggedInUser } = React.useContext(UserContext);
  const oePeriod = React.useContext(OEContext);

  const [showAllClients, setShowAllClients] = React.useState("Yes");
  const [allClientOption, setAllClientOption] = React.useState("All Clients");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [clientListSrc, setClientListSrc] = React.useState([]);
  const [clientMasterList, setClientMasterList] = React.useState([]);
  const [gridDataState, setGridDataState] = React.useState({
    take: 20,
    skip: 0,
  });
  const [showAuditTrailModelDialog, setShowAuditTrailModelDialog] =
    React.useState(false);
  const [selectedClient, setSelectedClient] = React.useState({});

  const pagerSetting = {
    buttonCount: 5,
    info: true,
    type: "numeric",
    pageSizes: [10, 20, 50, 100, 200, 500],
    previousNext: true,
  };

  const isBdm = !!loggedInUser?.bdmUserPrivilege;
  const isBroker = !!loggedInUser?.brokerUserPrivilege;
  const showBrokerRepColumns = isBdm || !isBroker;
  const showCostColumns = !loggedInUser?.hasAdminAppPrivilege;

  const { isLoading: clientListLoading, data: clientList } = useQueryGetClients(
    oePeriod?.oePeriodId,
    showAllClients,
    isBdm,
  );

  // One-time Aetna renewal notice. Asked across every client this user can
  // reach, not one client, because this screen is the client list. Clients and
  // brokers only - admin and BDM/sales users are excluded.
  const hasAnyAetnaClient = useAnyClientFeatureAccess(
    FEATURE_KEYS.Aetna,
    oePeriod?.oePeriodId,
  );
  // A client on the "blocked" list is filtered out of this list server-side,
  // so a blocked client lands here with an empty list. Show the admin-editable
  // portal message to them instead of the generic no-OE text.
  const noClientsMessage = useBlockedAwareMessage(
    oePeriod?.oePeriodId,
    "Currently there is no active Open Enrollment. Please reach out to Specialist for any additional info.",
  );
  const [showAetnaNotice, setShowAetnaNotice] = React.useState(false);
  const isClientOrBrokerUser =
    !!loggedInUser?.hasClientAppPrivilege &&
    !loggedInUser?.hasAdminAppPrivilege &&
    !isBdm;

  React.useEffect(() => {
    // The hook is false while the lookup is in flight and on error, so this
    // stays closed until the gate actually resolves to true.
    if (!hasAnyAetnaClient || !isClientOrBrokerUser) {
      return;
    }

    if (isAetnaNoticeDismissed(loggedInUser?.userId)) {
      return;
    }

    setShowAetnaNotice(true);
  }, [hasAnyAetnaClient, isClientOrBrokerUser, loggedInUser?.userId]);

  const handleAetnaNoticeClose = () => {
    markAetnaNoticeDismissed(loggedInUser?.userId);
    setShowAetnaNotice(false);
  };

  React.useEffect(() => {
    if (!clientListLoading && clientList && clientList.data) {
      // Filtering/sorting on the raw renewalHandlingMode field would let "Manual"
      // match rows whose cell actually renders blank (getRenewalBadge only shows a
      // badge when isNewBusinessRenewal is true). This derived label keeps the
      // filter/sort in sync with what the Renewal cell displays.
      const enriched = clientList.data.map((item) => ({
        ...item,
        renewalStatusLabel: getRenewalBadge(item)?.text || "",
      }));

      setClientMasterList(enriched);
      setClientListSrc(process(enriched.slice(0), gridDataState));
    }
  }, [clientListLoading, clientList, gridDataState]);

  React.useEffect(() => {
    if (loggedInUser && !loggedInUser.isWhiteListed) {
      navigate("/under-maintenance");
    }
  }, [loggedInUser, navigate]);

  if (clientListLoading) {
    return <LoadingIndicator />;
  }

  const onClientFilterChange = (event) => {
    setShowAllClients(event.value === "All Clients" ? "Yes" : "No");
    setAllClientOption(event.value);
  };

  const handleOpenClient = (item) => {
    setClient(item);
    if (loggedInUser.hasAdminAppPrivilege) {
      navigate(`/admin/clients/${item.clientId}/oe/${item.oePeriodId}/summary`);
    } else {
      navigate(`/clients/${item.clientId}/oe/${item.oePeriodId}/summary`);
    }
  };

  const handleOpenClientSetup = (item) => {
    setClient(item);
    navigate(`/admin/clients/${item.clientId}/setup`);
  };

  const handleAuditTrailModel = async (props) => {
    setSelectedClient(props);
    setShowAuditTrailModelDialog(true);
  };

  const RenderGrid = () => {
    const actionCell = (props) => {
      if (loggedInUser.hasAdminAppPrivilege) {
        return (
          <td>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "stretch",
              }}
            >
              <Button onClick={() => handleOpenClientSetup(props.dataItem)}>
                View Setup
              </Button>
              <Button
                themeColor="info"
                onClick={() => handleOpenClient(props.dataItem)}
                style={{ marginLeft: "10px" }}
              >
                View Rates
              </Button>
              <div
                style={{ paddingTop: "5px", marginLeft: "10px", width: "20px" }}
              >
                <FontAwesomeIcon
                  icon={faClockRotateLeft}
                  onClick={() => handleAuditTrailModel(props.dataItem)}
                  style={{
                    color: "grey",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                />
              </div>
            </div>
          </td>
        );
      } else {
        return (
          <td>
            <Button
              themeColor="info"
              onClick={() => handleOpenClient(props.dataItem)}
            >
              Review
            </Button>
          </td>
        );
      }
    };

    const renewalCell = (props) => {
      const badge = getRenewalBadge(props.dataItem);
      if (!badge) {
        return <td />;
      }
      const backgroundColor =
        badge.themeColor === "warning" ? "#b07d00" : "#0d6efd";
      return (
        <td>
          <span
            title={badge.title}
            style={{
              paddingLeft: "8px",
              paddingRight: "8px",
              paddingTop: "1px",
              paddingBottom: "1px",
              backgroundColor: backgroundColor,
              borderRadius: "15px",
              color: "white",
              fontSize: "12px",
            }}
          >
            {badge.text}
          </span>
        </td>
      );
    };

    const statusCell = (props) => {
      if (loggedInUser.hasAdminAppPrivilege) {
        if (props.dataItem.oeStatus === "NotStarted") {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "grey",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                {props.dataItem.oeStatusDesc}
              </span>
            </td>
          );
        } else if (props.dataItem.oeStatus === "InProgress") {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "orange",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                {props.dataItem.oeStatusDesc}
              </span>
            </td>
          );
        } else if (props.dataItem.oeStatus === "Submitted") {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "green",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                {props.dataItem.oeStatusDesc}
              </span>
            </td>
          );
        } else if (props.dataItem.oeStatus === "PublishError") {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "red",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                {props.dataItem.oeStatusDesc}
              </span>
            </td>
          );
        } else if (props.dataItem.oeStatus === "PublishInProgress") {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "yellow",
                  borderRadius: "15px",
                  color: "grey",
                  fontSize: "12px",
                }}
              >
                {props.dataItem.oeStatusDesc}
              </span>
            </td>
          );
        } else {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "darkgreen",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                {props.dataItem.oeStatusDesc}
              </span>
            </td>
          );
        }
      } else {
        if (props.dataItem.oeStatus === "NotStarted") {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "grey",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                {props.dataItem.oeStatusDesc}
              </span>
            </td>
          );
        } else if (props.dataItem.oeStatus === "InProgress") {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "orange",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                {props.dataItem.oeStatusDesc}
              </span>
            </td>
          );
        } else {
          return (
            <td>
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "green",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                {"Submitted"}
              </span>
            </td>
          );
        }
      }
    };

    const dataStateChange = (event) => {
      // A filter change has to land the user back on page one: keeping skip from
      // the old, larger result set points past the end of the filtered rows and
      // renders an empty grid.
      const normalize = (filter) =>
        filter?.filters?.length ? JSON.stringify(filter) : "";
      const filterChanged =
        normalize(event.dataState.filter) !== normalize(gridDataState.filter);
      const nextDataState = filterChanged
        ? { ...event.dataState, skip: 0 }
        : event.dataState;

      setClientListSrc(process(clientMasterList.slice(0), nextDataState));
      setGridDataState(nextDataState);
    };

    const columnMenuHeader = (field) => {
      var active = GridColumnMenuFilter.active(field, gridDataState.filter);
      return active ? "active" : "";
    };

    const ColumnCheckboxMenu = (props) => {
      return (
        <div>
          <GridColumnMenuCheckboxFilter
            {...props}
            data={clientMasterList}
            expanded={true}
          />
        </div>
      );
    };

    const ColumnMenu = (props) => {
      return (
        <div>
          <GridColumnMenuFilter
            {...props}
            expanded={true}
            hideSecondFilter={true}
          />
        </div>
      );
    };

    return (
      <Grid
        sortable={true}
        pageable={pagerSetting}
        pageSize={20}
        data={clientListSrc}
        {...gridDataState}
        onDataStateChange={dataStateChange}
      >
        <Column
          field="clientId"
          filter="text"
          columnMenu={ColumnMenu}
          headerClassName={columnMenuHeader("clientId")}
          title="Company ID"
        />
        <Column
          field="clientName"
          filter="text"
          columnMenu={ColumnMenu}
          headerClassName={columnMenuHeader("clientName")}
          title="Company Name"
        />
        <Column
          field="broker"
          filter="text"
          columnMenu={ColumnCheckboxMenu}
          headerClassName={columnMenuHeader("broker")}
          title="Broker"
        />
        {isBdm && (
          <Column
            field="generalAgent"
            filter="text"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("generalAgent")}
            title="GA"
          />
        )}
        {showBrokerRepColumns && (
          <Column
            field="benefitRepName"
            filter="text"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("benefitRep")}
            title="Benefit Rep"
          />
        )}
        {showBrokerRepColumns && (
          <Column
            field="salesRepName"
            filter="text"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("salesRepName")}
            title="Sales rep"
          />
        )}
        {showCostColumns && (
          <Column
            field="currentRate"
            filter="text"
            format="{0:c}"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("currentRate")}
            title="Current Cost"
          />
        )}
        {showCostColumns && (
          <Column
            field="percentageIncrease"
            filter="text"
            format="{0:p2}"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("percentageIncrease")}
            title="Percentage Increase"
          />
        )}
        {showBrokerRepColumns && (
          <Column
            field="wave"
            filter="text"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("wave")}
            title="Notification Wave"
          />
        )}
        {showBrokerRepColumns && (
          <Column
            field="notificationSent"
            filter="text"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("notificationSent")}
            title="Notification Sent"
          />
        )}

        <Column
          field="oeStatusDesc"
          filter="text"
          cell={statusCell}
          columnMenu={ColumnCheckboxMenu}
          headerClassName={columnMenuHeader("oeStatusDesc")}
          title="Status"
        />
        {!isBroker && (
          <Column
            field="renewalStatusLabel"
            width={"140"}
            title="Renewal"
            cell={renewalCell}
            filter="text"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("renewalStatusLabel")}
          />
        )}
        {isBdm && (
          <Column
            field="carrierName"
            width={"140"}
            title="Carrier"
            filter="text"
            columnMenu={ColumnCheckboxMenu}
            headerClassName={columnMenuHeader("carrierName")}
          />
        )}
        <Column
          field="Action"
          width={"240"}
          title="Action"
          cell={actionCell}
          sortable={false}
        />
        <Column
          field="submissionRecord"
          filter="text"
          columnMenu={ColumnMenu}
          headerClassName={columnMenuHeader("submissionRecord")}
          title="Submission Record"
        />
      </Grid>
    );
  };

  const RenderSearchBox = () => {
    const onChange = (event) => {
      setSearchTerm(event.target.value);

      let filters =
        gridDataState.filter && gridDataState.filter.filters.length > 0
          ? gridDataState.filter.filters
          : [];

      var clientNameField = filters.find((x) => x.field === "clientName");
      if (clientNameField) {
        clientNameField.value = event.target.value;
      } else {
        filters.push({
          field: "clientName",
          operator: "startswith",
          value: event.target.value,
        });
      }

      var clientIdField = filters.find((x) => x.field === "clientId");
      if (clientIdField) {
        clientIdField.value = event.target.value;
      } else {
        filters.push({
          field: "clientId",
          operator: "startswith",
          value: event.target.value,
        });
      }

      var SubmissionRecordField = filters.find(
        (x) => x.field === "submissionRecord",
      );
      if (SubmissionRecordField) {
        SubmissionRecordField.value = event.target.value;
      } else {
        filters.push({
          field: "submissionRecord",
          operator: "startswith",
          value: event.target.value,
        });
      }

      var brokerField = filters.find((x) => x.field === "broker");
      if (brokerField) {
        brokerField.value = event.target.value;
      } else {
        filters.push({
          field: "broker",
          operator: "startswith",
          value: event.target.value,
        });
      }

      var generalAgentField = filters.find((x) => x.field === "generalAgent");
      if (generalAgentField) {
        generalAgentField.value = event.target.value;
      } else {
        filters.push({
          field: "generalAgent",
          operator: "startswith",
          value: event.target.value,
        });
      }

      setGridDataState({
        ...gridDataState,
        filter: { logic: "or", filters: filters },
      });
    };

    return (
      <Input
        autoFocus
        type="search"
        key={"clientSearchBox"}
        onChange={onChange}
        placeholder="Search..."
        style={{ height: 40 }}
        value={searchTerm}
      />
    );
  };

  const hasActiveFilters = !!gridDataState.filter?.filters?.length;

  const onClearFilters = () => {
    setSearchTerm("");
    const clearedDataState = {
      ...gridDataState,
      skip: 0,
      filter: { logic: "and", filters: [] },
    };
    setClientListSrc(process(clientMasterList.slice(0), clearedDataState));
    setGridDataState(clearedDataState);
  };

  if (clientMasterList.length === 0 && showAllClients !== "No") {
    return (
      <div
        style={{
          backgroundColor: "#cce5ff",
          marginTop: 20,
          padding: 10,
          borderRadius: ".25rem",
          textAlign: "center",
        }}
      >
        <span style={{ color: "#004085" }}>
          {loggedInUser?.hasAdminAppPrivilege
            ? "Client Rate Model is not created yet. Please navigate to Workflow screen to create Client Rate Model."
            : loggedInUser?.bdmUserPrivilege
              ? "No clients are currently assigned to you in Salesforce. Please contact your administrator if you believe this is incorrect."
              : noClientsMessage}
        </span>
      </div>
    );
  }

  return (
    <div>
      {!loggedInUser.hasAdminAppPrivilege && (
        <div
          style={{
            backgroundColor: "#cce5ff",
            padding: "10px",
            marginTop: 20,
            borderRadius: ".25rem",
            textAlign: "center",
          }}
        >
          <span style={{ color: "#004085" }}>
            {loggedInUser.bdmUserPrivilege
              ? `Welcome to ${oePeriod.oePeriodName}. Below are your Salesforce-assigned clients.`
              : `Welcome to ${oePeriod.oePeriodName}. Please select your company below to review your current setup and create comparison models with new contributions.`}
          </span>
          <div style={{ color: "#004085", marginTop: "6px" }}>
            Renewal information will be displayed as carriers become available
            throughout Open Enrollment. If a carrier or client group is not
            currently shown, renewal information has not yet been released.
          </div>
        </div>
      )}
      <div
        style={{
          justifyContent: "space-between",
          display: "flex",
          paddingBottom: 20,
          marginTop: "20px",
        }}
      >
        <div style={{ width: "50%", display: "flex", flexDirection: "row" }}>
          {RenderSearchBox()}
          {loggedInUser.benefitAdminPrivilege && (
            <DropDownList
              style={{
                width: "150px",
                marginLeft: "10px",
              }}
              data={["All Clients", "My Clients"]}
              defaultValue={allClientOption}
              onChange={onClientFilterChange}
            />
          )}
        </div>
        <Button
          // Matches the checkbox menu's own "Filter" button, so Clear Filters
          // reads as the undo of that action rather than an unrelated color.
          themeColor={hasActiveFilters ? "primary" : "base"}
          style={{ whiteSpace: "nowrap" }}
          disabled={!hasActiveFilters}
          onClick={onClearFilters}
        >
          Clear Filters
        </Button>
      </div>
      {RenderGrid()}
      {showCostColumns && <MedicalOnlyCostNote />}
      {showAuditTrailModelDialog && (
        <AuditTrailModal
          client={selectedClient}
          onModelClose={() => setShowAuditTrailModelDialog(false)}
        />
      )}
      {showAetnaNotice && (
        <AetnaRenewalNoticeModal onClose={handleAetnaNoticeClose} />
      )}
    </div>
  );
};

export default ClientBrokerDashboard;
